// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import { AppPermission } from 'model/AppPermission';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useProjectsMetadata from './useProjectsMetadata';
import ParamsWithId from 'model/ParamsWithId';
import { UseCase } from '@tls/sw91-communication/types/com.base';

export default function useAppPermissions() {
  const { currentUser } = useAuthContext();
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);

  const hasUserPermission = useCallback(
    (permission: AppPermission) =>
      currentUser?.appPermissions?.easymodel ? currentUser.appPermissions.easymodel.includes(permission) : false,
    [currentUser?.appPermissions]
  );

  const hasPermissionForProject = useMemo(() => {
    return (
      (selectedProject?.projectUseCase?.useCase === UseCase.QUALITY_CHECK &&
        hasUserPermission(AppPermission.CreateQCProject)) ||
      (selectedProject?.projectUseCase?.useCase === UseCase.COMPONENT_DETECTION &&
        hasUserPermission(AppPermission.CreateCDProject))
    );
  }, [selectedProject?.projectUseCase?.useCase, hasUserPermission]);

  const hasCreateCDProjectPermission = hasUserPermission(AppPermission.CreateCDProject);
  const hasCreateQCProjectPermission = hasUserPermission(AppPermission.CreateQCProject);
  const hasSAMSegmentPermission = hasUserPermission(AppPermission.SAMSegment);
  const hasMultiLabelCDPermision = hasUserPermission(AppPermission.MultiLabelCD);
  const hasPreviewFeaturesPermission = hasUserPermission(AppPermission.PreviewFeatures);
  const hasExportVliTaiPermission = hasUserPermission(AppPermission.ExportVliTai);

  return {
    hasCreateCDProjectPermission,
    hasCreateQCProjectPermission,
    hasSAMSegmentPermission,
    hasMultiLabelCDPermision,
    hasPermissionForProject,
    hasPreviewFeaturesPermission,
    hasExportVliTaiPermission,
  };
}
